class SQuickAddModal extends HTMLElement {
  #ac;
  #fetchController;
  #dialog;
  #content;
  #status;
  #triggerElement;
  #productUrl = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#content = this.querySelector('[data-quick-add-content]');
    this.#status = this.querySelector('[data-quick-add-status]');

    if (!this.#dialog || !this.#content) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--open-product') this.#openProduct(event.source);
    }, opts);

    this.addEventListener('change', (event) => {
      this.#handleVariantChange(event);
    }, opts);

    this.addEventListener('submit', () => {
      if (this.#dialog?.open) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      this.#fetchController?.abort();
      this.removeAttribute('aria-busy');
      this.#restoreFocus();
    }, opts);

    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchController?.abort();
  }

  async #openProduct(source) {
    if (!(source instanceof HTMLElement)) return;

    const productUrl = source.dataset.productUrl;
    if (!productUrl) return;

    this.#triggerElement = source;
    this.#productUrl = productUrl;
    this.#setLoading();
    this.#showDialog();

    await this.#loadProduct(productUrl);
  }

  async #handleVariantChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const picker = target.closest('[data-quick-add-variant-picker]');
    if (!picker || !this.contains(picker)) return;

    const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId || !this.#productUrl) return;

    this.#syncVariantInput(variantId);

    const url = new URL(this.#productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    await this.#loadProduct(url.toString(), { focusVariantId: variantId, keepContent: true });
  }

  async #loadProduct(productUrl, { focusVariantId = null, keepContent = false } = {}) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const { signal } = this.#fetchController;

    this.setAttribute('aria-busy', 'true');
    if (!keepContent) this.#setLoading();

    try {
      const response = await fetch(productUrl, { signal });
      if (!response.ok) throw new Error(`Failed to load quick add product: ${response.status}`);

      const responseText = await response.text();
      signal.throwIfAborted();

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const quickAddSource = html.querySelector('[data-quick-add-source]');
      if (!quickAddSource) throw new Error('Quick add source was not found in product response.');

      const content = this.#prepareProductContent(quickAddSource);
      signal.throwIfAborted();

      this.#content.replaceChildren(content);
      this.#initializePaymentButtons();
      this.#announce('');
      this.removeAttribute('aria-busy');

      if (focusVariantId) this.#focusVariant(focusVariantId);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('[s-quick-add-modal] Product fetch failed:', error);
      this.removeAttribute('aria-busy');
      this.#setError();
    } finally {
      if (this.#fetchController?.signal === signal) this.#fetchController = null;
    }
  }

  #prepareProductContent(source) {
    const content = source.cloneNode(true);

    content.className = 'flex flex-col gap-6';
    content.removeAttribute('data-quick-add-source');

    for (const script of content.querySelectorAll('script')) script.remove();
    content.querySelector('#inventory-status')?.remove();
    // Product-page-hidden summary (photo, title, price) is shown only inside the modal.
    for (const summary of content.querySelectorAll('[data-quick-add-summary]')) summary.hidden = false;

    for (const picker of content.querySelectorAll('s-variant-picker')) {
      const replacement = document.createElement('div');
      for (const attribute of picker.attributes) {
        if (attribute.name === 'class') continue;
        replacement.setAttribute(attribute.name, attribute.value);
      }
      replacement.className = picker.className;
      replacement.setAttribute('data-quick-add-variant-picker', '');
      while (picker.firstChild) replacement.appendChild(picker.firstChild);
      picker.replaceWith(replacement);
    }

    return content;
  }

  #initializePaymentButtons() {
    if (!this.#content?.querySelector('.shopify-payment-button, [data-shopify-payment-button], shopify-accelerated-checkout')) return;

    window.Shopify?.PaymentButton?.init?.();
  }

  #showDialog() {
    if (!this.#dialog?.open) this.#dialog.showModal();
  }

  #setLoading() {
    const loadingText = this.dataset.loadingText || '';
    this.#announce(loadingText);
    this.#content?.replaceChildren(this.#messageNode(loadingText));
  }

  #setError() {
    const errorMessage = this.dataset.errorMessage || '';
    this.#announce(errorMessage);
    this.#content?.replaceChildren(this.#messageNode(errorMessage));
  }

  #messageNode(message) {
    const node = document.createElement('p');
    node.className = 'py-12 text-center text-sm text-on-surface-secondary';
    node.textContent = message;
    return node;
  }

  #announce(message) {
    if (this.#status) this.#status.textContent = message;
  }

  #syncVariantInput(variantId) {
    const form = this.#content?.querySelector('form');
    const input = form?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = variantId;
  }

  #focusVariant(variantId) {
    requestAnimationFrame(() => {
      for (const input of this.#content?.querySelectorAll('[data-variant-id]') || []) {
        if (input instanceof HTMLElement && input.dataset.variantId === variantId) {
          input.focus();
          return;
        }
      }
    });
  }

  #restoreFocus() {
    if (this.#triggerElement && typeof this.#triggerElement.focus === 'function') {
      this.#triggerElement.focus();
      this.#triggerElement = null;
    }
  }
}

if (!customElements.get('s-quick-add-modal')) {
  customElements.define('s-quick-add-modal', SQuickAddModal);
}
